import "./styles/theme.css";
import "./styles/layout.css";
import "./styles/auth.css";
import "./styles/replay.css";
import { isLoggedIn, restoreSession } from "./auth/auth";
import { migrateLegacyData } from "./auth/legacy";
import { showLogin, showHome, showAdmin } from "./auth/pages";
import { showTools } from "./tools/pages";
import { showAnalysis } from "./analysis/pages";
import { initTheme, toggleMode, themeIcon, currentMode } from "./core/appTheme";

import { DataStore } from "./data/DataStore";
import { Chart } from "./chart/Chart";
import { InputController, type PointerCtx } from "./chart/interaction/InputController";
import { DrawingLayer } from "./drawings/DrawingLayer";
import { TopToolbar } from "./ui/TopToolbar";
import { DrawingToolbar } from "./ui/DrawingToolbar";
import { Legend } from "./ui/Legend";
import { StylePopover } from "./ui/StylePopover";
import { openGoToDate } from "./ui/GoToDate";
import { toggleShortcuts } from "./ui/ShortcutsPanel";
import { openContextMenu } from "./ui/ContextMenu";
import { TOOL_DEFS } from "./drawings/registry";
import { createShortcutHandler } from "./input/shortcuts";
import { loadState, saveState } from "./state/AppState";
import { resolveZone, wireZoneIdChips, zoneIdChip, zoneIdOf } from "./practice/zoneId";
import type { ZoneInfo } from "./practice/types";
import { formatDateTime } from "./chart/util";

const SYMBOL = "MNQ";

async function boot() {
  const app = document.getElementById("app")!;
  app.innerHTML = `
    <div class="app-root">
      <div class="top-toolbar" id="top"></div>
      <div class="left-toolbar" id="left"></div>
      <div class="main-area" id="main">
        <div class="chart-host" id="host"></div>
        <div class="legend" id="legend"></div>
        <div class="loading-overlay" id="loading"><span class="spinner"></span>Loading market data…</div>
      </div>
    </div>`;

  const host = document.getElementById("host")!;
  const loading = document.getElementById("loading")!;

  const store = new DataStore(SYMBOL);
  const symbolMeta = await store.init();
  const timeframes = symbolMeta.timeframes;

  const chart = new Chart(host);
  chart.priceDecimals = 2;

  const layer = new DrawingLayer(chart, SYMBOL);
  chart.setDrawingLayer(layer);
  // dev handles for debugging in the console
  (window as any).__chart = chart;
  (window as any).__layer = layer;

  const input = new InputController(chart);
  input.delegate = layer;

  const legend = new Legend(document.getElementById("legend")!, SYMBOL);
  const stylePopover = new StylePopover(layer, chart);

  // persisted prefs
  const st = loadState();
  chart.crosshairMode = st.crosshair === false ? "hidden" : "normal";
  chart.showVolume = st.volume !== false;
  chart.showDSZone = st.dszone !== false;

  // ---- top toolbar ----
  const top = new TopToolbar(document.getElementById("top")!, {
    symbol: SYMBOL,
    timeframes,
    onSelectTf: (tf) => setTimeframe(tf, true),
    onGoToDate: () => actions.goToDate(),
    onToggleCrosshair: () => actions.toggleCrosshair(),
    onToggleVolume: () => actions.toggleVolume(),
    onToggleDSZone: () => actions.toggleDSZone(),
    onFullscreen: () => actions.fullscreen(),
    onShortcuts: () => toggleShortcuts(),
    onReset: () => actions.resetChart(),
    onFit: () => actions.fitData(),
  });
  top.setCrosshair(chart.crosshairMode === "normal");
  top.setVolume(chart.showVolume);
  top.setDSZone(chart.showDSZone);

  // nav links: replay + home
  const replayLink = document.createElement("button");
  replayLink.className = "tb-btn";
  replayLink.innerHTML = `<span style="font-size:12px;font-weight:600">⟲ Replay</span>`;
  replayLink.title = "Open Replay Trading";
  replayLink.addEventListener("click", () => (window.location.href = "/replay_trading"));
  document.getElementById("top")!.appendChild(replayLink);
  const homeLink = document.createElement("button");
  homeLink.className = "tb-btn";
  homeLink.innerHTML = `<span style="font-size:12px;font-weight:600">⌂ Home</span>`;
  homeLink.title = "Home";
  homeLink.addEventListener("click", () => (window.location.href = "/"));
  document.getElementById("top")!.appendChild(homeLink);

  // ---- find a zone by ID (button, or /charts?zone=ID) ----
  const zoneBtn = document.createElement("button");
  zoneBtn.className = "tb-btn";
  zoneBtn.innerHTML = `<span style="font-size:12px;font-weight:600">🔎 Zone</span>`;
  zoneBtn.title = "Go to a zone by its ID";
  zoneBtn.addEventListener("click", () => openZoneFinder());
  document.getElementById("top")!.appendChild(zoneBtn);
  wireZoneIdChips();
  let marked: ZoneInfo | null = null;
  chart.baseExtra.push((ctx, c) => {
    if (!marked || !c.data) return;
    const vp = c.viewport;
    const half = Math.max(1, vp.barSpacing / 2);
    const x0 = c.xOfTime(marked.legInTime) - half;
    const x1 = c.xOfTime(marked.signalTime) + half;
    const yA = vp.yOfPrice(marked.proximal);
    const yB = vp.yOfPrice(marked.distal);
    const top = Math.min(yA, yB);
    const h = Math.max(1, Math.abs(yB - yA));
    const rgb = marked.side === "long" ? "14,158,134" : "229,72,77";
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, vp.plot.width, vp.plot.height);
    ctx.clip();
    ctx.fillStyle = `rgba(${rgb},0.06)`;
    ctx.fillRect(x1, top, vp.plot.width - x1, h); // zone projected forward
    ctx.fillStyle = `rgba(${rgb},0.2)`;
    ctx.fillRect(x0, top, x1 - x0, h);
    ctx.strokeStyle = `rgba(${rgb},0.95)`;
    ctx.lineWidth = 2;
    ctx.strokeRect(Math.round(x0) + 0.5, Math.round(top) + 0.5, Math.round(x1 - x0), Math.round(h));
    ctx.font = "700 11px ui-monospace, Menlo, monospace";
    ctx.fillStyle = `rgb(${rgb})`;
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText("#" + (marked.zoneId ?? ""), x0, top - 4);
    ctx.restore();
  });
  const banner = document.createElement("div");
  banner.className = "zone-banner";
  banner.hidden = true;
  document.getElementById("main")!.appendChild(banner);
  const showZone = async (id: string): Promise<string | null> => {
    try {
      const r = await resolveZone(store, id);
      marked = r.zone;
      await setTimeframe(r.detectTf, false);
      await chart.goToIndex(r.index, { animate: true, spacing: Math.max(chart.viewport.barSpacing, 7) });
      chart.markDirty();
      banner.innerHTML = `${zoneIdChip(r.zone.zoneId!)} <b>${r.zone.type}</b> <span>${r.detectTf} · ${r.zone.side === "long" ? "▲ long" : "▼ short"} · ${formatDateTime(r.zone.signalTime)} ET</span>
        <a class="mini-btn primary" href="/practice?zone=${encodeURIComponent(r.zone.zoneId!)}">Practice this zone</a><button class="an-icon" data-close title="Clear">✕</button>`;
      banner.hidden = false;
      history.replaceState(null, "", `/charts?zone=${encodeURIComponent(r.zone.zoneId!)}`);
      return null;
    } catch (e) {
      return (e as Error).message;
    }
  };
  banner.addEventListener("click", (e) => {
    if (!(e.target as HTMLElement).closest("[data-close]")) return;
    marked = null;
    banner.hidden = true;
    chart.markDirty();
    history.replaceState(null, "", "/charts");
  });
  const openZoneFinder = () => {
    const bd = document.createElement("div");
    bd.className = "modal-backdrop";
    bd.innerHTML = `<form class="pr-dialog" autocomplete="off">
        <div class="pr-dialog-emoji">🔎</div>
        <div class="pr-dialog-title">Go to a zone</div>
        <div class="pr-dialog-body">Paste a zone ID from Practice or a test, e.g. <code>RBD-1m-SFZ9KW</code>.</div>
        <input class="pr-dialog-input" name="zid" placeholder="Zone ID" spellcheck="false" />
        <div class="auth-error" data-err></div>
        <div class="pr-dialog-actions"><button type="button" class="pr-dialog-btn" data-cancel>Cancel</button><button class="pr-dialog-btn is-primary">Go</button></div>
      </form>`;
    const close = () => bd.remove();
    bd.addEventListener("click", (e) => {
      if (e.target === bd || (e.target as HTMLElement).closest("[data-cancel]")) close();
    });
    bd.addEventListener("keydown", (e) => e.key === "Escape" && close());
    bd.querySelector("form")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const input = bd.querySelector<HTMLInputElement>("input")!;
      const err = bd.querySelector<HTMLElement>("[data-err]")!;
      err.textContent = "Finding…";
      const msg = await showZone(input.value);
      if (msg) err.textContent = msg;
      else close();
    });
    document.body.appendChild(bd);
    bd.querySelector<HTMLInputElement>("input")!.focus();
  };
  const themeBtn = document.createElement("button");
  themeBtn.className = "tb-btn";
  themeBtn.title = "Toggle light / dark";
  themeBtn.innerHTML = themeIcon(currentMode());
  themeBtn.addEventListener("click", () => {
    toggleMode();
    themeBtn.innerHTML = themeIcon(currentMode());
    chart.markDirty();
  });
  document.getElementById("top")!.appendChild(themeBtn);

  // ---- magnet preference (default ON, persisted per user) ----
  const magnetOn = st.magnet !== false;
  layer.setMagnet(magnetOn);

  // ---- left toolbar ----
  const leftToolbar = new DrawingToolbar(document.getElementById("left")!, {
    onSelect: (tool) => layer.setActiveTool(tool),
    onMagnet: (on) => {
      layer.setMagnet(on);
      saveState({ magnet: on });
    },
    onRemoveAll: () => layer.deleteAll(),
    getActive: () => layer.activeTool,
    initialMagnet: magnetOn,
  });
  leftToolbar.refresh();

  // ---- wiring ----
  layer.onActiveToolChange = () => {
    leftToolbar.refresh();
    stylePopover.update();
  };
  layer.onChange = () => stylePopover.update();
  chart.onViewportChange = () => {
    legend.update(chart);
    stylePopover.update();
  };
  chart.onCrosshairMove = () => legend.update(chart);

  // ---- timeframe switching ----
  let currentTf = "";
  let firstLoad = true;
  async function setTimeframe(tf: string, keepCenter: boolean) {
    const info = store.tfInfo(tf);
    if (!info || tf === currentTf) return;
    loading.style.display = "flex";
    const tfStore = await store.getTimeframe(tf);
    await chart.setTimeframe(tfStore, info.seconds, keepCenter && !firstLoad);
    currentTf = tf;
    firstLoad = false;
    top.setActiveTf(tf);
    legend.setTimeframe(info.label);
    legend.update(chart);
    saveState({ tf });
    loading.style.display = "none";
  }

  // ---- context menu ----
  // DSzone zones whose highlighted band covers bar `idx` → their zone IDs
  const zoneIdsAt = (idx: number): string[] => {
    const eng = chart.dszEngine;
    const data = chart.data;
    if (!eng || !data || idx < 0 || !chart.showDSZone) return [];
    const ids: string[] = [];
    for (let k = idx; k <= Math.min(chart.effBarCount - 1, idx + eng.maxSpan); k++) {
      const r = eng.at(k);
      if (r && k - r.span + 1 <= idx) for (const t of r.types) ids.push(zoneIdOf(t.name, currentTf, data.timeAt(k)));
    }
    return ids;
  };
  const flash = (msg: string) => {
    const t = document.createElement("div");
    t.className = "toast show";
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 1800);
  };

  input.onContextMenu = (cx, cy, ctx: PointerCtx) => {
    const hit = layer.pickAt(ctx.px, ctx.py);
    const items: any[] = [];
    for (const id of zoneIdsAt(ctx.index)) {
      items.push({
        label: `Copy zone ID  #${id}`,
        onClick: () => {
          navigator.clipboard?.writeText(id).catch(() => prompt("Zone ID:", id));
          flash(`Copied ${id}`);
        },
      });
      items.push({ label: `Practice this zone`, onClick: () => (location.href = `/practice?zone=${encodeURIComponent(id)}`) });
    }
    if (items.length) items.push("sep");
    if (hit) {
      layer.selectAt(ctx.px, ctx.py);
      if (hit.type === "text") items.push({ label: "Edit text", onClick: () => layer.editSelectedText() });
      items.push({ label: "Delete drawing", kbd: "Del", danger: true, onClick: () => layer.deleteSelected() });
      items.push("sep");
    }
    items.push({ label: "Reset price scale", onClick: () => chart.resetPriceScale() });
    items.push({ label: "Fit to data", kbd: "⇧R", onClick: () => actions.fitData() });
    items.push({ label: "Reset chart", kbd: "⌥R", onClick: () => actions.resetChart() });
    items.push({
      label: chart.crosshairMode === "hidden" ? "Show crosshair" : "Hide crosshair",
      kbd: "C",
      onClick: () => actions.toggleCrosshair(),
    });
    items.push("sep");
    items.push({ label: "Remove all drawings", danger: true, onClick: () => layer.deleteAll() });
    openContextMenu(cx, cy, items);
  };

  // ---- actions (shortcuts + toolbar) ----
  const actions: Record<string, (arg?: any) => void> = {
    zoomIn: () => chart.zoomBy(1.15, chart.plotWidth / 2),
    zoomOut: () => chart.zoomBy(1 / 1.15, chart.plotWidth / 2),
    panLeft: () => chart.panByBars(-chart.viewport.barsVisible * 0.15),
    panRight: () => chart.panByBars(chart.viewport.barsVisible * 0.15),
    priceUp: () => chart.shiftPrice(-30),
    priceDown: () => chart.shiftPrice(30),
    resetChart: () => chart.resetChart(),
    fitData: () => chart.fitToData(),
    goToDate: () => {
      if (!chart.data) return;
      const chunks = chart.data.meta.chunks;
      const minTime = chunks[0].startTime;
      const maxTime = chunks[chunks.length - 1].endTime;
      openGoToDate({
        minTime,
        maxTime,
        current: chart.centerTime() ?? maxTime,
        onGo: async (t) => {
          const idx = await chart.data!.timeToIndex(t);
          chart.goToIndex(idx, { animate: true });
        },
      });
    },
    toggleCrosshair: () => {
      chart.crosshairMode = chart.crosshairMode === "hidden" ? "normal" : "hidden";
      top.setCrosshair(chart.crosshairMode === "normal");
      chart.markOverlay();
      saveState({ crosshair: chart.crosshairMode === "normal" });
    },
    toggleVolume: () => {
      chart.showVolume = !chart.showVolume;
      top.setVolume(chart.showVolume);
      chart.markDirty();
      saveState({ volume: chart.showVolume });
    },
    toggleDSZone: () => {
      chart.showDSZone = !chart.showDSZone;
      top.setDSZone(chart.showDSZone);
      chart.markDirty();
      saveState({ dszone: chart.showDSZone });
    },
    fullscreen: () => {
      if (document.fullscreenElement) document.exitFullscreen();
      else document.documentElement.requestFullscreen().catch(() => {});
    },
    shortcuts: () => toggleShortcuts(),
    tfPrev: () => {
      const i = timeframes.findIndex((t) => t.tf === currentTf);
      if (i > 0) setTimeframe(timeframes[i - 1].tf, true);
    },
    tfNext: () => {
      const i = timeframes.findIndex((t) => t.tf === currentTf);
      if (i < timeframes.length - 1) setTimeframe(timeframes[i + 1].tf, true);
    },
    tfSlot: (slot: number) => {
      if (timeframes[slot]) setTimeframe(timeframes[slot].tf, true);
    },
    undo: () => layer.undo(),
    redo: () => layer.redo(),
    deleteSel: () => layer.deleteSelected(),
    deleteAll: () => layer.deleteAll(),
    cursor: () => layer.setActiveTool("cursor"),
  };
  // per-tool activation actions
  for (const id of Object.keys(TOOL_DEFS)) {
    actions["tool:" + id] = () => layer.setActiveTool(id);
  }

  window.addEventListener("keydown", createShortcutHandler(actions));

  // ---- initial timeframe ----
  const initialTf = st.tf && store.tfInfo(st.tf) ? st.tf : store.tfInfo("1m") ? "1m" : timeframes[timeframes.length - 1].tf;
  await setTimeframe(initialTf, false);
  const linkedZone = new URLSearchParams(location.search).get("zone");
  if (linkedZone) {
    const msg = await showZone(linkedZone);
    if (msg) alert(msg);
  }
}

function fail(e: any) {
  console.error(e);
  const loading = document.getElementById("loading");
  if (loading) loading.innerHTML = `<span style="color:var(--bear)">Failed to load: ${e?.message ?? e}</span>`;
}

// path router with a local login gate
async function route() {
  initTheme();
  migrateLegacyData(); // fold very old pre-account local data into the admin's space
  await restoreSession(); // Supabase session → cached profile → pull this user's cloud data
  if (!isLoggedIn()) {
    showLogin(() => route());
    return;
  }
  const path = window.location.pathname;
  if (path.startsWith("/replay_trading")) {
    import("./replay/replayMain").then((m) => m.bootReplay()).catch(fail);
  } else if (path.startsWith("/charts")) {
    boot().catch(fail);
  } else if (path.startsWith("/admin")) {
    showAdmin();
  } else if (path.startsWith("/tools")) {
    showTools();
  } else if (path.startsWith("/analysis")) {
    showAnalysis();
  } else if (path.startsWith("/tests") || path.startsWith("/test/")) {
    import("./tests/testsMain").then((m) => m.bootTests(path)).catch(fail);
  } else if (path.startsWith("/practice")) {
    import("./practice/practiceMain").then((m) => m.bootPractice()).catch(fail);
  } else {
    showHome();
  }
}
route().catch(fail);
